import {
  computed,
  ref,
  watch,
  type Ref,
} from 'vue'
import { debounce } from 'lodash'
import useContextStore from 'src/store/useContextStore'
import {
  ANALYSES_LPU_DEFAULT_SORT,
  getAnalysesLpuSortLabel,
  type AnalysesLpuSortValue,
} from 'src/components/pages/[town-translit]/diagnostika/analiz/_constants/analyses-lpu-sort'
import type { AnalysesPlaceItem } from 'src/components/pages/[town-translit]/diagnostika/analiz/api/apiAnalysesDistricts.types'
import type { AnalysesLpuCardsFilters } from 'src/components/pages/[town-translit]/diagnostika/analiz/api/apiAnalysesLpuCardsFilter.types'
import apiAnalysesLpuListCount
  from 'src/components/pages/[town-translit]/diagnostika/analiz/api/apiAnalysesLpuListCount'
import { buildAnalysesLpuListCountParams } from 'src/components/pages/[town-translit]/diagnostika/analiz/_functions/buildAnalysesLpuListCountParams'

type UseLpuCardsChipsOptions = {
  appliedSort: Ref<AnalysesLpuSortValue>
  appliedOnlineOrder: Ref<boolean>
  appliedWithDiscount: Ref<boolean>
  appliedPlace: Ref<AnalysesPlaceItem | undefined>
  onApply: (_filters: AnalysesLpuCardsFilters) => void
  onRequestError: (_error: unknown) => void
}

export default function useLpuCardsChips({
  appliedSort,
  appliedOnlineOrder,
  appliedWithDiscount,
  appliedPlace,
  onApply,
  onRequestError,
}: UseLpuCardsChipsOptions) {
  const { context } = useContextStore()
  const townId = context.town?.id

  const isFiltersDrawerOpen = ref(false)
  const isPlaceDrawerOpen = ref(false)
  const draftSort = ref<AnalysesLpuSortValue>(appliedSort.value)
  const draftPlace = ref<AnalysesPlaceItem | undefined>(appliedPlace.value)
  const draftOnlineOrder = ref(appliedOnlineOrder.value)
  const draftWithDiscount = ref(appliedWithDiscount.value)
  const filtersCount = ref<number>()
  const isCountLoading = ref(false)

  let filtersCountRequestId = 0

  const appliedSortLabel = computed(() => getAnalysesLpuSortLabel(appliedSort.value))

  const draftFilters = computed<AnalysesLpuCardsFilters>(() => ({
    sort: draftSort.value,
    place: draftPlace.value,
    onlineOrder: draftOnlineOrder.value,
    withDiscount: draftWithDiscount.value,
  }))

  const hasUnsavedFilters = computed(() => (
    draftSort.value !== appliedSort.value
    || draftPlace.value?.translit !== appliedPlace.value?.translit
    || draftOnlineOrder.value !== appliedOnlineOrder.value
    || draftWithDiscount.value !== appliedWithDiscount.value
  ))

  const hasAppliedFilters = computed(() => Boolean(
    appliedSort.value !== ANALYSES_LPU_DEFAULT_SORT
    || appliedPlace.value
    || appliedOnlineOrder.value
    || appliedWithDiscount.value,
  ))

  const hasNonDefaultDraftFilters = computed(() => Boolean(
    draftSort.value !== ANALYSES_LPU_DEFAULT_SORT
    || draftPlace.value
    || draftOnlineOrder.value
    || draftWithDiscount.value,
  ))

  const showResetFiltersButton = computed(() => (
    hasAppliedFilters.value || hasNonDefaultDraftFilters.value
  ))

  function getCurrentFilters(
    overrides: Partial<AnalysesLpuCardsFilters> = {},
    sort: AnalysesLpuSortValue = draftSort.value,
  ): AnalysesLpuCardsFilters {
    return {
      sort,
      onlineOrder: draftOnlineOrder.value,
      withDiscount: draftWithDiscount.value,
      place: draftPlace.value,
      ...overrides,
    }
  }

  async function loadFiltersCount() {
    if (!isFiltersDrawerOpen.value || !hasUnsavedFilters.value) {
      filtersCount.value = undefined
      isCountLoading.value = false
      return
    }

    filtersCountRequestId += 1

    const requestId = filtersCountRequestId

    try {
      const count = await fetchFiltersCount()

      if (requestId !== filtersCountRequestId) {
        return
      }

      filtersCount.value = count
    } catch (error) {
      if (requestId !== filtersCountRequestId) {
        return
      }

      filtersCount.value = undefined
      onRequestError(error)
    } finally {
      if (requestId === filtersCountRequestId) {
        isCountLoading.value = false
      }
    }
  }

  async function fetchFiltersCount(): Promise<number> {
    if (townId == null) {
      throw new Error('town_id is required for analyses lpu list count')
    }

    const { count } = await apiAnalysesLpuListCount.get(
      buildAnalysesLpuListCountParams({
        sort: draftSort.value,
        onlineOrder: draftOnlineOrder.value,
        withDiscount: draftWithDiscount.value,
        place: draftPlace.value,
      }, townId),
    )

    return count
  }

  const debouncedLoadFiltersCount = debounce(loadFiltersCount, 250)

  function scheduleFiltersCountLoad() {
    if (!isFiltersDrawerOpen.value || !hasUnsavedFilters.value) {
      filtersCount.value = undefined
      isCountLoading.value = false
      debouncedLoadFiltersCount.cancel()
      return
    }

    isCountLoading.value = true
    debouncedLoadFiltersCount()
  }

  function syncDraftFromApplied() {
    draftSort.value = appliedSort.value
    draftPlace.value = appliedPlace.value
    draftOnlineOrder.value = appliedOnlineOrder.value
    draftWithDiscount.value = appliedWithDiscount.value
  }

  function openFiltersDrawer() {
    isFiltersDrawerOpen.value = true
  }

  function openPlaceDrawer() {
    isPlaceDrawerOpen.value = true
  }

  function onPlaceApply(place?: AnalysesPlaceItem) {
    updateDraftFilters({ place })

    if (!isFiltersDrawerOpen.value) {
      onApply(getCurrentFilters({ place }))
    }
  }

  function onDrawerSortSelect(value: AnalysesLpuSortValue) {
    updateDraftFilters({ sort: value })
  }

  function updateDraftFilters(partial: Partial<AnalysesLpuCardsFilters>) {
    if ('sort' in partial) {
      draftSort.value = partial.sort || ANALYSES_LPU_DEFAULT_SORT
    }

    if ('place' in partial) {
      draftPlace.value = partial.place
    }

    if ('onlineOrder' in partial) {
      draftOnlineOrder.value = Boolean(partial.onlineOrder)
    }

    if ('withDiscount' in partial) {
      draftWithDiscount.value = Boolean(partial.withDiscount)
    }

    scheduleFiltersCountLoad()
  }

  function applyFilters() {
    onApply(getCurrentFilters())
    isFiltersDrawerOpen.value = false
  }

  function resetDraftFilters() {
    updateDraftFilters({
      sort: ANALYSES_LPU_DEFAULT_SORT,
      place: undefined,
      onlineOrder: false,
      withDiscount: false,
    })
  }

  function resetAppliedPlace() {
    draftPlace.value = undefined
    onApply(getCurrentFilters({ place: undefined }))
  }

  function resetAppliedOnlineOrder() {
    draftOnlineOrder.value = false
    onApply(getCurrentFilters({ onlineOrder: false }))
  }

  function resetAppliedWithDiscount() {
    draftWithDiscount.value = false
    onApply(getCurrentFilters({ withDiscount: false }))
  }

  watch(isFiltersDrawerOpen, isOpen => {
    if (isOpen) {
      syncDraftFromApplied()
      scheduleFiltersCountLoad()
      return
    }

    isPlaceDrawerOpen.value = false
    filtersCount.value = undefined
    isCountLoading.value = false
  })

  watch(appliedSort, sort => {
    draftSort.value = sort
  })

  watch(appliedOnlineOrder, isEnabled => {
    draftOnlineOrder.value = isEnabled
  })

  watch(appliedWithDiscount, isEnabled => {
    draftWithDiscount.value = isEnabled
  })

  watch(appliedPlace, place => {
    draftPlace.value = place
  })

  watch(
    [
      draftSort,
      draftPlace,
      draftOnlineOrder,
      draftWithDiscount,
      hasUnsavedFilters,
    ],
    () => {
      scheduleFiltersCountLoad()
    },
  )

  return {
    townId,
    isFiltersDrawerOpen,
    isPlaceDrawerOpen,
    draftSort,
    draftPlace,
    draftFilters,
    filtersCount,
    isCountLoading,
    appliedSortLabel,
    hasUnsavedFilters,
    hasAppliedFilters,
    showResetFiltersButton,
    getCurrentFilters,
    openFiltersDrawer,
    openPlaceDrawer,
    onPlaceApply,
    onDrawerSortSelect,
    updateDraftFilters,
    applyFilters,
    resetDraftFilters,
    resetAppliedPlace,
    resetAppliedOnlineOrder,
    resetAppliedWithDiscount,
  }
}
